import axios from 'axios'
import moment from 'moment'
import chunk from 'lodash.chunk'
import union from 'lodash.union'
import isEqual from 'lodash.isequal'
import { missingRequiredFields, failedToPassRule } from '@/Throws/Errors'
import UsersCollection from '@/modules/Accounts/models/Users.collection'

/**
 * Parse a list of users from text
 *
 * @param {String} text
 * @param {Boolean} includesHeaders
 * @param {Array} requiredFields
 * @param {Array} rules
 * @param {String} rowSplit
 * @param {String} colSplit
 * @returns {Array}
 */
export const parseFromText = (text, includesHeaders, requiredFields, rules, rowSplit, colSplit) => {
  const users = []
  let headers = []
  let userRows = []
  const rows = text.split(rowSplit)

  if (rows.length === 0) {
    throw new Error('Incorrect format')
  }

  const split = () => rows.map(row => {
    return row.split(colSplit).map(col => col.trim())
  }).filter(row => row.length !== 0 && row.every(col => col.length !== 0))

  if (includesHeaders) {
    [headers, ...userRows] = split()
    const missingRequired = requiredFields.filter(field => !headers.includes(field))
    if (missingRequired.length !== 0) {
      throw missingRequiredFields(missingRequired)
    }
  } else {
    headers = requiredFields
    userRows = split()
  }

  userRows.forEach((row, rowIndex) => {
    const user = row.reduce((acc, attribute, i) => {
      const field = headers[i]
      if (field in rules) {
        rules[field].forEach(fn => {
          const result = fn(attribute)
          if (result !== true) {
            throw failedToPassRule(result, rowIndex + 1)
          }
        })
      }
      acc[field] = attribute
      return acc
    }, {})
    users.push(user)
  })

  return users
}

/**
 * Get single account data in the format to be passed to /api/v1/users/{username}
 *
 * @param formData
 * @param taskGroupId
 * @returns {Object}
 */
export const getSingleAccountAPIData = (formData) => {
  const record = filterNullValues(formData.user)

  if (formData.assessmentConfigId) {
    record['assessment_config_id'] = formData.assessmentConfigId
  } else if (formData.customConfig) {
    record['assessment_config'] = formData.customConfig
  }

  if (formData.user.client_version_id === null) {
    record['client_version_id'] = null
  }

  return record
}

/**
 * Get the API data to send a POST request to /api/v1/users/assessment
 *
 * @param atsProfileIds
 * @param userData
 * @returns {{update_assessment: *, update_user: *, filter_user: {ids: *}}}
 */
export const getCreateAssessmentAPIData = (atsProfileIds, userData) => {
  let users = []
  for (let i = 0; i < userData.length; ++i) {
    let user = {
      username: userData[i].username,
      assessment_date: userData[i].assessment_date,
      assessment_start_date: userData[i].assessment_start_date,
      assessment_end_date: userData[i].assessment_end_date,
      assessment_location: userData[i].assessment_location,
      assessment_group: userData[i].assessment_group,
      assessment_config_id: userData[i].assessment_config_id,
      assessment_config: userData[i].assessment_config,
      assessment_duration_modifier: userData[i].assessment_duration_modifier,
      assessment_skip_scheduling: userData[i].assessment_skip_scheduling,
      client_version_id: userData[i].client_version_id
    }

    if (atsProfileIds[i]) {
      user['ats_profile_id'] = atsProfileIds[i]
    }

    users.push(user)
  }

  return { 'users': users }
}

/**
 * Get the API data to send a PUT request to /api/v1/assessments
 *
 * @param userIds
 * @param userData
 */
export const getUpdateAssessmentAPIData = (userIds, user, assessmentsStarted) => {
  let assessmentData = null

  if (assessmentsStarted) {
    // We can only update location and group and skip_tech_checks if an assessment has started
    assessmentData = {
      location: user.assessment_location,
      assessment_group: user.assessment_group,
      skip_tech_checks: user.assessment_skip_tech_checks
    }
  } else {
    assessmentData = {
      assessment_date: user.assessment_date,
      start_date: user.assessment_start_date,
      end_date: user.assessment_end_date,
      location: user.assessment_location,
      assessment_group: user.assessment_group,
      config: user.assessment_config,
      assessment_config_id: user.assessment_config_id,
      duration_modifier: user.assessment_duration_modifier,
      skip_scheduling: user.assessment_skip_scheduling,
      skip_tech_checks: user.assessment_skip_tech_checks,
      client_version_id: user.client_version_id
    }

    if (user.assessment_config != null && user.assessment_config.tasks != null && user.assessment_config.tasks.names.length > 0) {
      assessmentData.task_group_id = null
    }
  }

  return {
    filter_user: {
      ids: userIds
    },
    update_user: filterNullValues({
      'location': user.location,
      'email': user.email
    }),
    update_assessment: filterNullValues(assessmentData)
  }
}

/**
 * Get accounts data in the format to be passed as a call to /api/v1/users
 *
 * @param formData
 * @param taskGroupId
 * @returns {{users: *}}
 */
export const getAccountsAPIData = (formData) => {
  const usernames = ('users' in formData && formData.users.length > 0)
    ? formData.users.map(user => user.username)
    : [formData.user.username]

  const updateData = getSingleAccountAPIData(formData)

  return {
    users: usernames.map(username => {
      return {
        ...updateData,
        username
      }
    })
  }
}

/**
 * Search accounts
 *
 * @param limit
 * @param offset
 * @param query
 * @param cancelToken
 * @returns {Promise<any>}
 */
export const searchAccounts = ({ limit = 1, offset = 0, ...query }, cancelToken) => {
  return new Promise((resolve, reject) => {
    queryAccounts({ limit, offset, ...query }, cancelToken)
      .then(responses => {
        let total = 0
        let users = []
        const result = union(...responses.map(resp => resp['data']['users']))

        if ('usernames' in query) {
          total = result.length
          users = result.slice(offset, (limit === -1) ? total : (limit + offset))
        } else {
          total = responses[0]['data'].total
          users = result
        }

        resolve({ users, total })
      })
      .catch((resp) => {
        let errMessage = resp.data || resp.message
        return reject(errMessage)
      })
  })
}

/**
 * Given a list of usernames return duplicates in the list as well as usernames that already exist in the database
 *
 * @param usernames
 * @returns {Promise<any>}
 */
export const findDuplicates = (usernames) => {
  return new Promise((resolve) => {
    if (usernames.length === 0) {
      resolve([])
    }
    const query = { usernames, limit: usernames.length, exact: true }
    queryAccounts(query)
      .then(responses => {
        if (responses.length === 0) {
          resolve([])
        }
        const duplicates = union(responses.map(resp => resp['data']['users'].map(user => user.username)))[0]
        const selfDuplicates = usernames.filter((username, idx) => usernames.indexOf(username) < idx)
        resolve(duplicates.concat(selfDuplicates))
      })
      .catch(() => {
        resolve([])
      })
  })
}

/**
 * Batch post requests to create new users
 *
 * @param data
 * @param taskGroupId
 * @returns {Promise<any>}
 */
export const batchCreateAccounts = (data) => {
  const chunkSize = 100
  const query = getAccountsAPIData(data)

  // CSV UPLOAD USED
  if (data.csv.filename) {
    const headers = Object.assign({}, ...data.csv.headers
      .filter(header => !!header.field)
      .map(header => {
        return { [header.field]: header.value }
      }))

    const mapper = {
      assessment_location: 'location',
      location: 'location',
      assessment_group_1: 'group_id'
    }

    query.users.forEach(user => {
      const csvUser = data.csv.users.find(u => user.username === u[headers.username])
      Object.keys(mapper).forEach(field => {
        user[field] = csvUser[headers[mapper[field]]]
      })
    })
  }

  // Chunk users to be created in batches
  const requests = chunk(query.users, chunkSize).map(batch => {
    return axios.post('/api/v1/users', { users: batch })
  })

  return Promise.all(requests)
    .then((responses) => {
      return responses.reduce((acc, resp) => acc.concat(resp.data.users), [])
    })
}

/**
 * Batch get request to /api/v1/users
 *
 * @param query
 * @param cancelToken
 * @returns {Promise<[any]>}
 */
export const queryAccounts = (query, cancelToken) => {
  const apiException = (err) => {
    throw Error(`Accounts query request failed. \n${err}`)
  }

  if (!('usernames' in query)) {
    const queryString = new URLSearchParams(query)
    return Promise.all([axios.get(`/api/v1/users?${queryString}`, { cancelToken })]).catch(apiException)
  }

  if (typeof query.usernames === 'string') {
    query.usernames = query.usernames.split(',')
  }

  const chunkSize = (query.limit === -1 || query.limit > 20) ? 20 : query.limit
  query.offset = 0

  const requests = chunk(query.usernames, chunkSize).map(batch => {
    const queryString = new URLSearchParams({ ...query, usernames: batch })
    return axios.get(`/api/v1/users?${queryString}`, { cancelToken })
  })

  return Promise.all(requests).catch(apiException)
}

/**
 * Given a list of accounts passed
 * return which accounts are valid
 *
 * @param accounts
 * @returns {Promise<any[]>}
 */
export const verifyAccounts = (accounts) => {
  const reflect = p => p.then(v => ({ ...v, status: 'success' }),
    e => ({ ...e.response, status: 'error' }))

  const requests = accounts.map((account) => {
    return axios.post(`/authenticate/verify?tk=${account.token}`, {})
  })

  return Promise.all(requests.map(reflect))
}

/**
 * Get data to be passed to the Accounts form the selected accounts on the Update page
 *
 * @param selectedAccounts
 * @returns {{user, users: *, commonTaskGroup: boolean}}
 */
export const getUpdateAccountsData = (selectedAccounts) => {
  const user = {}

  const firstAssessment = selectedAccounts.length > 0 ? selectedAccounts[0].assessments[0] : null

  // Pass common attributes
  if (selectedAccounts.length > 0) {
    const commonAttributes = Object.keys(selectedAccounts[0]).filter(attribute => {
      return selectedAccounts.every(account => account[attribute] === selectedAccounts[0][attribute])
    })
    for (const attribute of commonAttributes) {
      user[attribute] = selectedAccounts[0][attribute]
    }

    user.expires_at = user.expires_at
      ? moment(user.expires_at).format('YYYY-MM-DD')
      : null
  }

  // Pass assessment_location
  const assessmentLocations = selectedAccounts.map(account => {
    return account.assessments.length !== 0
      ? account.assessments[0].location
      : account.location
  })
  if (assessmentLocations.every(location => location === assessmentLocations[0])) {
    user.assessment_location = assessmentLocations[0]
  }

  // Pass assessment_group
  const assessmentGroup1 = selectedAccounts.map(account => {
    return account.assessments.length !== 0
      ? account.assessments[0].assessment_group
      : account.assessment_group
  })
  if (assessmentGroup1.every(location => location === assessmentGroup1[0])) {
    user.assessment_group = assessmentGroup1[0]
  }

  // Pass assessment modifier
  const assessmentModifier = selectedAccounts.map(account => {
    return account.assessments.length !== 0
      ? account.assessments[0].duration_modifier
      : null
  })
  if (assessmentModifier.every(modifier => modifier === assessmentModifier[0])) {
    user.assessment_duration_modifier = assessmentModifier[0]
  }

  // Pass skip scheduling
  const skipScheduling = selectedAccounts.map(account => {
    return account.assessments.length !== 0
      ? account.assessments[0].skip_scheduling
      : null
  })
  if (skipScheduling.every(skip => skip === skipScheduling[0])) {
    user.assessment_skip_scheduling = skipScheduling[0]
  }

  // Pass assessment start date
  const assessmentStartDates = selectedAccounts.map(account => {
    return account.assessments.length !== 0
      ? account.assessments[0].start_date
      : null
  })
  if (assessmentStartDates.every(startDate => startDate === assessmentStartDates[0])) {
    user.assessment_start_date = assessmentStartDates[0]
    user.isAssessmentStartDateNullForAll = (assessmentStartDates[0] === null)
  }

  // Pass assessment end date
  const assessmentEndDates = selectedAccounts.map(account => {
    return account.assessments.length !== 0
      ? account.assessments[0].end_date
      : null
  })
  if (assessmentEndDates.every(endDate => endDate === assessmentEndDates[0])) {
    user.assessment_end_date = assessmentEndDates[0]
  }

  if (selectedAccounts.length > 0 && selectedAccounts.every(account => account.assessments[0].assessment_date === firstAssessment.assessment_date)) {
    user.assessment_date = firstAssessment.assessment_date
    user.isAssessmentDateNullForAll = firstAssessment.assessment_date === null
  }

  // Pass common assessment_config_id
  const commonAssessmentConfigId = selectedAccounts.length > 0 && selectedAccounts.every(account => account.assessments[0].assessment_config_id === firstAssessment.assessment_config_id)
    ? firstAssessment.assessment_config_id
    : -1
  if (selectedAccounts.length > 0 && selectedAccounts.every(account => isEqual(account.assessments[0].config || {}, firstAssessment.config || {}))) {
    user.assessment_config = firstAssessment.config
  }

  // Pass common client version
  if (selectedAccounts.length > 0 && selectedAccounts.every(account => account.assessments[0].client_version_id === firstAssessment.client_version_id)) {
    user.client_version_id = firstAssessment.client_version_id
  }

  return {
    user,
    users: selectedAccounts.map(account => ({
      id: account.id,
      assessments: account.assessments,
      username: account.username,
      token: account.token ? account.token : false,
      assessment_location: account.assessments[0].location,
      assessment_group: account.assessments[0].assessment_group,
      assessment_duration_modifier: account.assessments[0].duration_modifier,
      assessment_skip_scheduling: account.assessments[0].skip_scheduling,
      assessment_start_date: account.assessments[0].start_date,
      assessment_end_date: account.assessments[0].end_date,
      assessment_date: account.assessments[0].assessment_date,
      client_version_id: account.assessments[0].client_version_id
    })),
    usersCollection: new UsersCollection(selectedAccounts),
    commonAssessmentConfigId
  }
}

export const getTechCheckResults = (userIds) => {
  return axios.get(`/api/v1/preassessment/tech_check?user_id=${userIds.join(',')}`)
    .then((result) => {
      return result.data.items
    })
}

export const searchAssessments = ({ limit = 10, offset = 0, ...query }, cancelToken) => {
  const queryString = new URLSearchParams({ offset: offset, limit: limit, ...query })

  return axios.get(`/api/v1/assessments?${queryString}`, { cancelToken })
    .then(responses => {
      return { assessments: responses['data']['assessments'], total: responses['data']['total'] }
    })
    .catch((resp) => {
      let errMessage = resp.data || resp.message
      throw errMessage
    })
}

export const clearSessionData = async (mode, assessmentIds) => {
  for (let i = 0; i < assessmentIds.length; i++) {
    await axios.post(`/api/v1/assessments/${assessmentIds[i]}/reset`, { mode: mode })
  }
}

export const sendScoringRequest = async (assessmentIds, scoringConfiguration) => {
  let scoringConfigParams = scoringConfiguration
  for (let i = 0; i < assessmentIds.length; i++) {
    if (scoringConfiguration === null) {
      // it is possible to send a null type scoringConfig but this will fail JSON decoding on the Plaform API on Flask 2.x
      // so we set it to an empty object
      scoringConfigParams = {}
    }
    await axios.post(`/api/v1/assessments/${assessmentIds[i]}/rescore`, scoringConfigParams)
  }
}

export const assessmentStatus = (assessment) => {
  if (assessment.expires_at !== assessment.end_date) {
    if (moment(assessment.expires_at).isAfter(moment()) > 0) {
      return 'In Progress'
    } else {
      return 'Completed'
    }
  }
  if (moment(assessment.expires_at).isBefore(moment()) < 0) {
    return 'Expired'
  }
  if (assessment.assessment_date) {
    return 'Scheduled'
  }
  if (assessment.is_active) {
    return 'Active'
  }
  return 'Replaced'
}

export const getActiveAssessment = (account) => {
  return account.assessments.find((assessment) => {
    return assessment.is_active
  })
}

function filterNullValues (object) {
  let result = {}

  for (let key in object) {
    if (object[key] !== null) {
      result[key] = object[key]
    }
  }

  return result
}
